import * as Sentry from '@sentry/browser'
import get from 'lodash/get'
import {parseJWT, getToken, getLoginCase, getInteractionUid} from 'utils/stringUtils'
import {isProfileApp, isConsentApp, isLoginApp, isBrowser} from 'utils/envUtils'
import {getClient} from 'theme'
import {isEmpty} from 'utils/objectUtils'
import {BRANDS} from 'const'

export const getBrand = () => {
  let brand
  if (isConsentApp() || isLoginApp()) {
    brand = getClient(isBrowser() ? window : null)
  } else if (isProfileApp()) {
    const brandPath = isBrowser() ? window.location.pathname.substring(1) : null
    if (brandPath?.includes('global')) {
      brand = BRANDS.onelog
    } else {
      for (const b of Object.values(BRANDS)) {
        if (brandPath?.includes(b)) {
          brand = b
          break
        }
      }
    }
  }

  return brand === BRANDS.onelog ? BRANDS.onelog : brand
}

const extractLoginCase = () => {
  if (isConsentApp() || isLoginApp()) {
    return getLoginCase(window.location.search)
  }
}

const extractRoute = () => get(window, 'location.pathname')

const getExtras = (info = {}) => {
  const brand = getBrand()
  const loginCase = extractLoginCase()
  const route = extractRoute()
  let extras = {}

  if (brand) extras.brand = brand
  if (loginCase) extras.loginCase = loginCase
  if (route) extras.route = route
  if (!isEmpty(info)) extras = {...extras, ...info}

  return !isEmpty(extras) ? extras : null
}

export const init = () => {
  if (process.env.REACT_APP_SENTRY_KEY) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_KEY,
      environment: process.env.REACT_APP_ENV,
      release: process.env.REACT_APP_SENTRY_RELEASE_NAME,
      denyUrls: [
        /otBannerSdk\.js/,
        /facebook\.com/i,
        /^(?:(?!google\.com).)*$/,
        /googletagmanager\.com/i,
        /google-analytics\.com/i,
        /doubleclick\.net/i,
      ],
      beforeSend(event) {
        // Remove user emails for privacy
        if (event.user) {
          delete event.user.email
        }
        // Drop non-critical events (info, debug, (*)warnings)
        if (event.level && ['info', 'debug', 'warning'].includes(event.level)) {
          return null
        }

        // Ignore CORS and network errors
        if (event.exception?.values) {
          const message = event.exception.values[0].value || ''
          if (
            message.includes('NetworkError') ||
            message.includes('Failed to fetch') ||
            message.includes('Script error')
          ) {
            return null
          }
        }
        return event
      },
      ignoreErrors: [
        // Network-related errors (caused by browser or user actions)
        'TypeError: Failed to fetch',
        'TypeError: NetworkError when attempting to fetch resource.',
        'TypeError: Cancelled',
        'TypeError: cancelled',
        'TypeError: Abgebrochen',
        'TypeError: annulé',
        'TypeError: avbrutt',
        'Network Error',

        // Common 3rd-party script errors
        'Script error.',
        'ResizeObserver loop limit exceeded',
        /Loading chunk \d+ failed/,
        /ReferenceError:.*/,
        /SecurityError:.*/,

        // Google Analytics
        /gtag\..* is not a function/,
        /googletag\..* is not a function/,
        /fbq\..* is not a function/,
      ],
    })

    Sentry.getCurrentScope().setTag('interactionId', getInteractionUid(window.location.search))

    try {
      const token = getToken(window.location.hash)
      if (token) {
        const payload = parseJWT(token)
        Sentry.getCurrentScope().setUser({
          id: payload.sub,
          email: payload.email,
        })
      }
    } catch (e) {}
  }
}

// TODO think about PII https://docs.sentry.io/data-management/sensitive-data/
export const error = (error, info) => {
  if (process.env.REACT_APP_SENTRY_KEY) {
    Sentry.withScope(scope => {
      const extras = getExtras(info)
      extras && scope.setExtras(extras)
      Sentry.captureException(error)
    })
  }
}
